import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { useDialog } from '@shared/hooks/useDialog';
import { Card, Badge, Button, Input } from '@shared/components/ui';
import { Shield, Plus, Trash2, X, Users } from 'lucide-react';

interface CustomRole {
  id: string;
  organization_id: string;
  name: string;
  permissions: Record<string, boolean>;
  created_at: string;
  updated_at: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  role: string;
}

const PERMISSIONS_LIST = [
  { key: 'backups:read', labelKey: 'customRoles.permissionLabels.backupsRead' },
  { key: 'backups:write', labelKey: 'customRoles.permissionLabels.backupsWrite' },
  { key: 'backups:delete', labelKey: 'customRoles.permissionLabels.backupsDelete' },
  { key: 'backups:trigger', labelKey: 'customRoles.permissionLabels.backupsTrigger' },
  { key: 'backups:restore', labelKey: 'customRoles.permissionLabels.backupsRestore' },
  { key: 'agents:read', labelKey: 'customRoles.permissionLabels.agentsRead' },
  { key: 'agents:write', labelKey: 'customRoles.permissionLabels.agentsWrite' },
  { key: 'agents:delete', labelKey: 'customRoles.permissionLabels.agentsDelete' },
  { key: 'schedules:read', labelKey: 'customRoles.permissionLabels.schedulesRead' },
  { key: 'schedules:write', labelKey: 'customRoles.permissionLabels.schedulesWrite' },
  { key: 'schedules:delete', labelKey: 'customRoles.permissionLabels.schedulesDelete' },
] as const;

const createEmptyPermissions = (): Record<string, boolean> =>
  PERMISSIONS_LIST.reduce<Record<string, boolean>>((acc, permission) => {
    acc[permission.key] = false;
    return acc;
  }, {});

export default function CustomRoles() {
  const { t, i18n } = useTranslation('settings');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showConfirm } = useDialog();
  const organizationId = searchParams.get('org_id') || '';

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);
  const [selectedOrg, setSelectedOrg] = useState(organizationId);
  const rolesRequestRef = useRef(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const modalReturnFocusRef = useRef<HTMLElement | null>(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [newPermissions, setNewPermissions] =
    useState<Record<string, boolean>>(createEmptyPermissions);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [organizationsError, setOrganizationsError] = useState(false);
  const [success, setSuccess] = useState('');

  const token = getToken();

  const getPermissionLabel = (permissionKey: string) => {
    const permission = PERMISSIONS_LIST.find(({ key }) => key === permissionKey);
    return permission ? t(permission.labelKey) : permissionKey;
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (organizationId) {
      setSelectedOrg(organizationId);
    }
  }, [organizationId]);
  useEffect(() => {
    setCustomRoles([]);
    if (selectedOrg) void fetchCustomRoles(selectedOrg);
    return () => {
      rolesRequestRef.current += 1;
    };
  }, [selectedOrg]);

  useEffect(() => {
    if (showCreateModal) return;
    const trigger = modalReturnFocusRef.current;
    if (trigger?.isConnected) {
      modalReturnFocusRef.current = null;
      trigger.focus();
    }
  }, [showCreateModal]);

  const fetchOrganizations = async () => {
    setLoading(true);
    setError('');
    setOrganizationsError(false);
    try {
      const response = await fetch('/api/v1/organizations', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to load organizations');
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data.organizations || []);
        if (!selectedOrg && data.organizations?.length > 0) {
          const firstOrgId = data.organizations[0].id;
          setSelectedOrg(firstOrgId);
        }
      }
    } catch {
      setOrganizationsError(true);
      setError(t('organizations.errors.loadListFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomRoles = async (orgId: string) => {
    const requestId = ++rolesRequestRef.current;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/v1/custom-roles?organization_id=${orgId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        if (requestId !== rolesRequestRef.current) return;
        setCustomRoles(Array.isArray(data) ? data : []);
      } else {
        if (requestId !== rolesRequestRef.current) return;
        setError(t('customRoles.error.fetchFailed'));
      }
    } catch {
      if (requestId === rolesRequestRef.current) setError(t('customRoles.error.fetchFailed'));
    } finally {
      if (requestId === rolesRequestRef.current) setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) {
      setError(t('customRoles.error.nameRequired'));
      return;
    }

    if (!Object.values(newPermissions).some((v) => v)) {
      setError(t('customRoles.selectOnePermission'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/v1/custom-roles?organization_id=${selectedOrg}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newRoleName,
          permissions: newPermissions,
        }),
      });

      if (response.ok) {
        setSuccess(t('customRoles.success.created'));
        setShowCreateModal(false);
        setNewRoleName('');
        setNewPermissions(createEmptyPermissions());
        await fetchCustomRoles(selectedOrg);
      } else {
        const data = await response.json();
        setError(data.error || t('customRoles.error.createFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    const confirmed = await showConfirm(
      t('customRoles.deleteConfirmTitle'),
      t('customRoles.deleteConfirmMessage'),
      'warning'
    );
    if (!confirmed) return;

    setLoading(true);
    try {
      const response = await fetch(`/api/v1/custom-roles/delete?role_id=${roleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setSuccess(t('customRoles.success.deleted'));
        await fetchCustomRoles(selectedOrg);
      } else {
        setError(t('customRoles.error.deleteFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-300">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-text-primary-light dark:text-text-primary-dark sm:text-4xl">
              {t('customRoles.title')}
            </h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('customRoles.subtitle')}
            </p>
          </div>
          <Button
            type="button"
            onClick={(event) => {
              modalReturnFocusRef.current = event.currentTarget;
              setShowCreateModal(true);
            }}
            size="lg"
            className="min-h-11 w-full shadow-brand-200 sm:w-auto"
          >
            <Plus className="mr-2 h-5 w-5" strokeWidth={3} /> {t('customRoles.create')}
          </Button>
        </div>

        {/* Organization Selector */}
        <Card className="p-4 flex items-center gap-4 bg-surface-2-light dark:bg-surface-2-dark border-surface-border-light dark:border-surface-border-dark">
          <Users className="text-text-subtle-light dark:text-text-subtle-dark" />
          <div className="flex-1">
            <label
              htmlFor="org-select"
              className="block text-xs font-bold text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider mb-1"
            >
              {t('customRoles.activeOrganization', 'Active Organization')}
            </label>
            <select
              id="org-select"
              value={selectedOrg}
              onChange={(e) => {
                const id = e.target.value;
                setSelectedOrg(id);
                navigate(`/custom-roles?org_id=${id}`);
              }}
              className="min-h-11 w-full cursor-pointer rounded-lg border border-surface-border-light bg-surface-card-light px-3 text-base font-semibold text-text-primary-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:border-surface-border-dark dark:bg-surface-card-dark dark:text-text-primary-dark"
            >
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
        </Card>

        {(error || success) && (
          <div
            role={error ? 'alert' : 'status'}
            aria-live={error ? 'assertive' : 'polite'}
            className={`flex items-center justify-between gap-4 p-4 rounded-xl border font-bold text-sm ${
              error
                ? 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20 text-state-danger-light dark:text-state-danger-dark'
                : 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20 text-state-ok-light dark:text-state-ok-dark'
            }`}
          >
            <span>{error || success}</span>
            {error && (
              <button
                type="button"
                onClick={() =>
                  void (organizationsError ? fetchOrganizations() : fetchCustomRoles(selectedOrg))
                }
                disabled={loading || (!organizationsError && !selectedOrg)}
                className="shrink-0 rounded-lg border border-current/30 px-3 py-2 font-bold hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current disabled:opacity-60 dark:hover:bg-white/5"
              >
                {t('common:actions.retry')}
              </button>
            )}
          </div>
        )}

        {/* Roles Grid */}
        {loading && customRoles.length === 0 ? (
          <div className="py-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
            <p className="text-text-subtle-light dark:text-text-subtle-dark font-bold uppercase tracking-widest text-xs">
              {t('customRoles.loading')}
            </p>
          </div>
        ) : customRoles.length === 0 && !error ? (
          <Card className="py-24 text-center border-2 border-dashed">
            <div className="w-20 h-20 bg-surface-2-light dark:bg-surface-2-dark rounded-3xl flex items-center justify-center mx-auto mb-6 text-text-subtle-light dark:text-text-subtle-dark">
              <Shield size={40} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('customRoles.empty')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark mt-2 max-w-sm mx-auto font-medium text-sm">
              {t('customRoles.emptyDescription')}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {customRoles.map((role) => (
              <Card key={role.id} className="p-6 group">
                <div className="flex justify-between items-start gap-3 mb-6">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="w-12 h-12 shrink-0 rounded-2xl bg-accent-soft-light dark:bg-accent-soft-dark flex items-center justify-center text-brand-600 dark:text-brand-400">
                      <Shield size={24} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="break-words font-bold text-lg text-slate-900 dark:text-white">
                        {role.name}
                      </h3>
                      <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-wider">
                        {t('customRoles.createdAt', {
                          date: new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, {
                            dateStyle: 'medium',
                          }).format(new Date(role.created_at)),
                        })}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRole(role.id)}
                    aria-label={`${t('customRoles.delete')} ${role.name}`}
                    className="min-h-11 min-w-11 shrink-0 text-text-subtle-light dark:text-text-subtle-dark hover:text-state-danger-light dark:hover:text-state-danger-dark"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>

                <div className="space-y-3">
                  <p className="text-xs font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-2">
                    {t('customRoles.permissions')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(role.permissions)
                      .filter(([_, v]) => v)
                      .map(([key]) => (
                        <Badge key={key} variant="neutral" className="text-[11px] font-medium">
                          {getPermissionLabel(key)}
                        </Badge>
                      ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="custom-role-dialog-title"
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                setShowCreateModal(false);
              }
              if (event.key === 'Tab') {
                const focusable = event.currentTarget.querySelectorAll<HTMLElement>(
                  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
                );
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                  event.preventDefault();
                  last?.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                  event.preventDefault();
                  first?.focus();
                }
              }
            }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-3 backdrop-blur-sm animate-in fade-in duration-300 sm:p-4"
          >
            <Card className="w-full max-w-2xl shadow-elevated max-h-[90vh] overflow-y-auto custom-scrollbar">
              <div className="space-y-5 p-4 sm:space-y-6 sm:p-8">
                <div className="flex items-center justify-between gap-3">
                  <h2
                    id="custom-role-dialog-title"
                    className="text-xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-2xl"
                  >
                    {t('customRoles.newRole')}
                  </h2>
                  <button
                    type="button"
                    aria-label={t('common.close', 'Close')}
                    onClick={() => setShowCreateModal(false)}
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg hover:bg-surface-2-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:hover:bg-surface-2-dark"
                  >
                    <X size={20} className="text-text-subtle-light dark:text-text-subtle-dark" />
                  </button>
                </div>

                <Input
                  id="custom-role-name"
                  label={t('customRoles.name')}
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder={t('customRoles.namePlaceholder')}
                  required
                  autoComplete="off"
                  autoFocus
                />

                <fieldset>
                  <legend className="mb-3 text-sm font-semibold text-text-muted-light dark:text-text-muted-dark">
                    {t('customRoles.permissions')}
                  </legend>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {PERMISSIONS_LIST.map((permission) => (
                      <label
                        key={permission.key}
                        className="flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border border-surface-border-light p-3 transition-colors hover:bg-surface-2-light focus-within:ring-2 focus-within:ring-brand-600 dark:border-surface-border-dark dark:hover:bg-surface-2-dark"
                      >
                        <input
                          type="checkbox"
                          className="h-5 w-5 shrink-0 accent-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
                          checked={newPermissions[permission.key]}
                          onChange={() =>
                            setNewPermissions((previous) => ({
                              ...previous,
                              [permission.key]: !previous[permission.key],
                            }))
                          }
                        />
                        <span className="min-w-0 break-words text-sm font-medium leading-5 text-slate-700 dark:text-slate-300">
                          {t(permission.labelKey)}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="flex gap-3 pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
                  <Button
                    variant="secondary"
                    className="min-h-11 flex-1"
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                  >
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button
                    className="min-h-11 flex-1"
                    onClick={handleCreateRole}
                    isLoading={loading}
                  >
                    {t('customRoles.create')}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
